/**
 * Manage kid profiles — add, edit, remove.
 * Accessed at /routines/kids
 */

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Baby, Plus, Trash2, Pencil, X, Loader2, Flame } from 'lucide-react';
import { api } from '@/lib/api';
import { useMyHomes } from '@/hooks/useMe';
import EmojiPicker from '@/components/EmojiPicker';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface KidProfile {
  id: string; homeId: string; name: string;
  avatarEmoji: string; color: string;
  dateOfBirth: string | null; notes: string | null;
  isActive: boolean;
  currentStreakDays: number; totalCheckins: number; routineCount: number;
}

// EMOJI_OPTIONS removed — EmojiPicker now handles selection
const COLOR_OPTIONS = ['#22c55e', '#3b82f6', '#ec4899', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#10b981'];

export default function KidsPage() {
  const { data: homes } = useMyHomes();
  const homeId = homes?.homes?.[0]?.home.id;
  const qc = useQueryClient();
  const [editing, setEditing] = useState<KidProfile | null>(null);
  const [showForm, setShowForm] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['kids', homeId],
    queryFn: () => api.get<{ kids: KidProfile[] }>(`/homes/${homeId}/kids`),
    enabled: !!homeId,
  });

  const kids = data?.kids ?? [];

  const delMut = useMutation({
    mutationFn: (id: string) => api.delete(`/homes/${homeId}/kids/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kids', homeId] }),
  });

  const remove = (k: KidProfile) => {
    if (!confirm(`Remove ${k.name}'s profile? Their routines and history will be removed too.`)) return;
    delMut.mutate(k.id);
  };

  if (!homeId) return null;

  return (
    <div className="space-y-5 pb-20">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Baby className="w-5 h-5 text-emerald-500" /> Kid profiles
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">For children without their own Kejja account. Parents manage everything.</p>
        </div>
        <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }}>
          <Plus className="w-4 h-4 mr-1" /> Add kid
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : kids.length === 0 ? (
        <Card><CardContent className="py-10 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-3">
            <Baby className="w-6 h-6 text-emerald-600" />
          </div>
          <div className="text-sm font-medium mb-1">No kids added yet</div>
          <div className="text-xs text-muted-foreground mb-4 max-w-sm mx-auto">
            Add your children's profiles so they can track routines, earn streaks, and work towards rewards.
          </div>
          <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }}>
            <Plus className="w-4 h-4 mr-1" /> Add first kid
          </Button>
        </CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {kids.map(k => (
            <Card key={k.id} className={cn(!k.isActive && 'opacity-60')}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-14 h-14 rounded-full flex items-center justify-center text-3xl flex-shrink-0"
                     style={{ background: k.color + '20' }}>
                  {k.avatarEmoji}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold truncate">{k.name}</div>
                    {!k.isActive && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">inactive</span>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                    <span className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
                      <Flame className="w-3 h-3" /> {k.currentStreakDays}d
                    </span>
                    <span>{k.routineCount} routines</span>
                    <span>{k.totalCheckins} check-ins</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setEditing(k); setShowForm(true); }}
                          className="w-8 h-8 rounded hover:bg-muted flex items-center justify-center">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(k)} disabled={delMut.isPending}
                          className="w-8 h-8 rounded hover:bg-muted text-rose-600 flex items-center justify-center">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showForm && (
        <KidFormDialog
          homeId={homeId}
          editing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['kids', homeId] }); setShowForm(false); setEditing(null); }}
        />
      )}
    </div>
  );
}

function KidFormDialog({ homeId, editing, onClose, onSaved }: {
  homeId: string; editing: KidProfile | null; onClose: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [avatarEmoji, setAvatarEmoji] = useState(editing?.avatarEmoji ?? '🧒');
  const [color, setColor] = useState(editing?.color ?? '#22c55e');
  const [dob, setDob] = useState(editing?.dateOfBirth ?? '');
  const [notes, setNotes] = useState(editing?.notes ?? '');
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);

  const mut = useMutation({
    mutationFn: () => {
      const body = { name, avatarEmoji, color, dateOfBirth: dob || null, notes: notes || null,
                     ...(editing ? { isActive } : {}) };
      if (editing) return api.patch(`/homes/${homeId}/kids/${editing.id}`, body);
      return api.post(`/homes/${homeId}/kids`, body);
    },
    onSuccess: onSaved,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    mut.mutate();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
      <div className="bg-background w-full max-w-md rounded-t-xl md:rounded-xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">{editing ? 'Edit kid profile' : 'Add a kid'}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded hover:bg-muted flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center text-4xl"
                 style={{ background: color + '20' }}>
              {avatarEmoji}
            </div>
            <div className="flex-1">
              <Label className="text-xs">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Liz, Kai, Alex…" required className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Avatar</Label>
            <div className="mt-1">
              <EmojiPicker value={avatarEmoji} onChange={setAvatarEmoji} variant="avatar" color={color} size="lg" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Color</Label>
            <div className="flex gap-2 mt-1">
              {COLOR_OPTIONS.map(c => (
                <button key={c} type="button"
                  onClick={() => setColor(c)}
                  className={cn('w-8 h-8 rounded-full border-2', color === c ? 'border-foreground scale-110' : 'border-transparent')}
                  style={{ background: c }} />
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Date of birth (optional)</Label>
            <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. allergies, interests" className="mt-1" />
          </div>
          {editing && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              Active
            </label>
          )}
        </form>
        <div className="p-4 border-t flex gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button type="submit" onClick={submit} disabled={mut.isPending} className="flex-1">
            {mut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? 'Save' : 'Add kid'}
          </Button>
        </div>
      </div>
    </div>
  );
}
